import { lazy, Suspense } from "react";
import { useOutletContext } from "react-router-dom";
import { useSite } from "@/lib/site";
import SEO from "@/components/SEO";
import Hero from "@/components/Hero";

// Lazy load below-the-fold components to reduce initial JS payload.
const LogoMarquee = lazy(() => import("@/components/LogoMarquee"));
const StatsSection = lazy(() => import("@/components/StatsSection"));
const DeliveryPrincipleSection = lazy(() => import("@/components/DeliveryPrincipleSection"));
const ServicesGrid = lazy(() => import("@/components/ServicesGrid"));
const IndustriesGrid = lazy(() => import("@/components/IndustriesGrid"));
const CaseStudiesSlider = lazy(() => import("@/components/CaseStudiesSlider"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const TechExpertise = lazy(() => import("@/components/TechExpertise"));
const InsightsTeaser = lazy(() => import("@/components/InsightsTeaser"));
const LeadCTA = lazy(() => import("@/components/LeadCTA"));
const ValuesSection = lazy(() => import("@/components/ValuesSection"));
const ComplianceSection = lazy(() => import("@/components/ComplianceSection"));
const ShowcaseSection = lazy(() => import("@/components/ShowcaseSection"));
const PartnersSection = lazy(() => import("@/components/PartnersSection"));

const DEFAULT_HOME_SECTIONS = [
  "hero",
  "clients",
  "stats",
  "delivery_principles",
  "services",
  "industries",
  "case_studies",
  "testimonials",
  "tech",
  "insights",
  "compliance",
  "showcase",
  "partners",
  "lead_cta",
].map((key) => ({ key }));

const INITIAL_HOME_SECTIONS = DEFAULT_HOME_SECTIONS.slice(0, 1);

const RENDERERS = {
  hero: (ctx) => <Hero onBookCall={ctx.openBooking} />,
  clients: () => <LogoMarquee />,
  partners: () => <PartnersSection />,
  stats: () => <StatsSection />,
  delivery_principles: () => <DeliveryPrincipleSection />,
  services: () => <ServicesGrid />,
  industries: () => <IndustriesGrid compact />,
  values: () => <ValuesSection />,
  compliance: () => <ComplianceSection />,
  showcase: () => <ShowcaseSection />,
  case_studies: () => <CaseStudiesSlider />,
  testimonials: () => <TestimonialsSection />,
  tech: () => <TechExpertise />,
  insights: () => <InsightsTeaser />,
  lead_cta: (ctx) => <LeadCTA onBookCall={ctx.openBooking} />,
};

// Fallback skeleton for lazy loaded sections
const SectionFallback = () => <div className="h-32 bg-transparent"></div>;

export default function HomePage() {
  const ctx = useOutletContext();
  const { site, loading } = useSite();
  const configuredSections = site?.homepage_sections || [];
  // Keep the Hero in the same keyed tree while site configuration loads. This
  // avoids remounting the LCP candidate and prevents every below-fold chunk
  // from being requested during the critical first render.
  const sections = loading
    ? INITIAL_HOME_SECTIONS
    : configuredSections.length > 0
      ? configuredSections
      : DEFAULT_HOME_SECTIONS;

  return (
    <div data-testid="home-page">
      <SEO slug="home" />
      {sections.map((s) => {
        const fn = RENDERERS[s.key];
        if (!fn) return null;
        return (
          <Suspense fallback={<SectionFallback />} key={s.key}>
            <div data-testid={`section-${s.key}`}>{fn(ctx)}</div>
          </Suspense>
        );
      })}
    </div>
  );
}
